import { useEffect, useMemo, useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { enqueueSnackbar } from 'notistack';
import FormFieldBlock from 'components/form/Block';
import Input from 'components/form/Input';
import Select from 'components/form/Select';
import LoadingIndicator from 'components/LoadingIndicator';
import { getPayoutAccountQueryKey, useGetPayoutAccountQuery } from 'api/queries/escrow/get-payout-account';
import { useGetPayoutAccountTypesQuery } from 'api/queries/escrow/get-payout-account-types';
import { useGetPayoutChannelsQuery } from 'api/queries/escrow/get-payout-channels';
import { useUpsertPayoutAccountMutation } from 'api/mutations/escrow/upsert-payout-account';
import {
  createPayoutAccountSchema,
  getAccountNumberFieldMeta,
  PayoutAccountFormValues,
  toFormValues,
} from 'utils/payout-account-schema';
import { getProviderLabel } from 'utils/provider-labels';
import { memoryQueryClient } from 'providers/TanstackQuery';

export default function PayoutAccountSettings() {
  const { data: payoutAccount, isLoading: accountLoading } = useGetPayoutAccountQuery();
  const { data: accountTypes = [], isLoading: typesLoading } = useGetPayoutAccountTypesQuery();
  const { mutateAsync: upsertPayoutAccount, isPending } = useUpsertPayoutAccountMutation();
  const [isEditing, setIsEditing] = useState(false);

  const schema = useMemo(() => createPayoutAccountSchema(accountTypes), [accountTypes]);

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<PayoutAccountFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      account_type: '',
      account_name: '',
      account_number: '',
      channel_code: undefined,
    },
  });

  const accountTypeController = useController({ control, name: 'account_type' });
  const channelCodeController = useController({ control, name: 'channel_code' });
  const watchedAccountType = watch('account_type');
  const watchedChannelCode = watch('channel_code');

  const selectedType = accountTypes.find((t) => t.slug === watchedAccountType);
  const isMultiChannel = selectedType?.channelCode === null;

  const resolvedAccountType =
    payoutAccount?.accountType ?? accountTypes.find((t) => t.id === payoutAccount?.accountTypeId);

  const accountNumberMeta = getAccountNumberFieldMeta(selectedType?.slug);

  const { data: channels = [] } = useGetPayoutChannelsQuery(selectedType?.slug, { enabled: isMultiChannel });
  const selectedChannel = channels.find((ch) => ch.channelCode === watchedChannelCode);

  const isAccountTypeDisabled =
    !!resolvedAccountType?.slug &&
    accountTypes.length > 0 &&
    !accountTypes.some((t) => t.slug === resolvedAccountType.slug);

  useEffect(() => {
    if (payoutAccount && accountTypes.length > 0) {
      reset(toFormValues(payoutAccount, accountTypes));
    }
  }, [payoutAccount, accountTypes, reset]);

  const onSubmit = async (formData: PayoutAccountFormValues) => {
    try {
      const type = accountTypes.find((t) => t.slug === formData.account_type);
      const account = await upsertPayoutAccount({
        accountType: formData.account_type,
        accountName: formData.account_name,
        accountNumber: formData.account_number,
        channelCode: type?.channelCode === null ? (formData.channel_code ?? null) : null,
      });
      memoryQueryClient.setQueryData(getPayoutAccountQueryKey(), account);
      enqueueSnackbar('Payout account saved successfully.', { variant: 'success' });
      setIsEditing(false);
    } catch {
      // Error already handled by mutation's onError
    }
  };

  const onCancel = () => {
    if (payoutAccount && accountTypes.length > 0) {
      reset(toFormValues(payoutAccount, accountTypes));
    } else {
      reset({ account_type: '', account_name: '', account_number: '', channel_code: undefined });
    }
    setIsEditing(false);
  };

  const showForm = isEditing || !payoutAccount;

  if (typesLoading || accountLoading) {
    return (
      <div className="flex flex-1 flex-col px-6 lg:px-0 lg:pl-10 pt-4">
        <LoadingIndicator size={24} />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col px-6 lg:px-0 lg:pl-10">
      <div className="flex flex-col w-full md:w-[260px]">
        <div className="h-2" />

        {showForm ? (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-4">
              <FormFieldBlock label="Account Type" error={errors.account_type?.message}>
                <Select
                  {...accountTypeController.field}
                  items={accountTypes.map((t) => ({ id: t.slug, label: t.label, value: t.slug }))}
                  value={
                    selectedType
                      ? { id: selectedType.slug, label: selectedType.label, value: selectedType.slug }
                      : undefined
                  }
                  onChange={(item) => {
                    const newSlug = item?.id ?? '';
                    accountTypeController.field.onChange(newSlug);
                    const newType = accountTypes.find((t) => t.slug === newSlug);
                    if (newType?.channelCode !== null) {
                      setValue('channel_code', undefined);
                    }
                  }}
                  placeholder="Select account type"
                  hasError={!!errors.account_type}
                />
              </FormFieldBlock>

              {isMultiChannel && (
                <FormFieldBlock label="Select Bank" error={errors.channel_code?.message}>
                  <Select
                    {...channelCodeController.field}
                    items={channels.map((ch) => ({ id: ch.channelCode, label: ch.channelName, value: ch.channelCode }))}
                    value={
                      selectedChannel
                        ? {
                            id: selectedChannel.channelCode,
                            label: selectedChannel.channelName,
                            value: selectedChannel.channelCode,
                          }
                        : undefined
                    }
                    onChange={(item) => channelCodeController.field.onChange(item?.id)}
                    placeholder="Select bank"
                    hasError={!!errors.channel_code}
                  />
                </FormFieldBlock>
              )}

              <FormFieldBlock label="Account Name" error={errors.account_name?.message}>
                <Input {...register('account_name')} placeholder="Full name on account" className="w-full" />
              </FormFieldBlock>

              <FormFieldBlock label={accountNumberMeta.label} error={errors.account_number?.message}>
                <Input {...register('account_number')} placeholder={accountNumberMeta.placeholder} className="w-full" />
              </FormFieldBlock>

              <div className="flex flex-row gap-2 pt-2">
                {payoutAccount && (
                  <button
                    type="button"
                    onClick={onCancel}
                    disabled={isPending}
                    className="flex flex-row justify-center rounded-[40px] items-center text-xs text-brand-black font-semibold whitespace-nowrap h-6 px-4 bg-brand-purple-card"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  disabled={isPending}
                  className="flex flex-row justify-center rounded-[40px] items-center text-xs text-white font-semibold whitespace-nowrap h-6 px-4 bg-brand-black"
                >
                  {isPending ? <LoadingIndicator size={14} className="mr-1" /> : null}
                  Save
                </button>
              </div>
            </div>
          </form>
        ) : (
          <div className="flex flex-col gap-4">
            {isAccountTypeDisabled && (
              <p className="text-xs text-[#FF6B00]">
                Your account type is no longer available. Please select a new type to update your payout account.
              </p>
            )}

            <FormFieldBlock label="Account Type">
              <div className="text-sm font-semibold">{resolvedAccountType?.label ?? '\u2014'}</div>
            </FormFieldBlock>

            <FormFieldBlock label="Provider">
              <span className="inline-block rounded-full bg-gray-100 text-gray-700 text-xs font-semibold px-3 py-1">
                {getProviderLabel(payoutAccount.driver)}
              </span>
            </FormFieldBlock>

            <FormFieldBlock label="Account Name">
              <div className="text-sm font-semibold">{payoutAccount.accountName}</div>
            </FormFieldBlock>

            <FormFieldBlock label="Account Number">
              <div className="text-sm font-semibold">{payoutAccount.accountNumber}</div>
            </FormFieldBlock>

            <FormFieldBlock label="Channel">
              <div className="text-sm font-semibold">{payoutAccount.channelCode ?? '\u2014'}</div>
            </FormFieldBlock>

            <FormFieldBlock label="Status">
              {payoutAccount.isVerified ? (
                <span className="bg-green-100 text-green-700 rounded-full px-3 py-1 text-xs font-semibold self-start">
                  Verified
                </span>
              ) : (
                <span className="bg-[#FFA800]/10 text-[#FF6B00] rounded-full px-3 py-1 text-xs font-semibold self-start">
                  Pending Verification
                </span>
              )}
            </FormFieldBlock>

            <div className="pt-2">
              <button
                type="button"
                onClick={() => {
                  if (isAccountTypeDisabled) {
                    setValue('account_type', '');
                  }
                  setIsEditing(true);
                }}
                className="flex flex-row justify-center rounded-[40px] items-center text-xs text-white font-semibold whitespace-nowrap h-6 px-4 bg-brand-black"
              >
                {isAccountTypeDisabled ? 'Update' : 'Edit'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
